import { buildOpenTrialPayload } from "@decibeltrade/sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAnalytics } from "@/contexts/analytics-context";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import type { FftTrialOpenEventProps } from "@/lib/analytics";
import { Logger } from "@/utils/logger";

import { readDexSdk } from "../../dex-sdk";
import { writeActiveTrial } from "../../queries/funded-trial/active-trial";
import { fundedTrialEligibilityQueryKey } from "../../queries/funded-trial/eligibility";
import { usePrimarySessionKeyDex } from "../../write-dex-sdk-context";
import { useWalletFeePayerWithFallback } from "../wallet-fee-payer";
import { requireCampaignAddrs } from "./campaign-addrs";

/** Analytics-only — the payload needs nothing beyond wallet + campaign context. */
export type OpenTrialArgs = FftTrialOpenEventProps;

/**
 * Open a Funded First Trade — the side is picked on-chain via randomness.
 * Signed silently by the primary-subaccount session key when one exists;
 * wallet prompt otherwise.
 * No optimistic write: `trial_id` is only known post-submit; the WS push/refetch
 * delivers the row.
 */
export function useOpenTrial() {
  const queryClient = useQueryClient();
  const { accountAddress } = useDecibelWallet();
  const primaryDex = usePrimarySessionKeyDex();
  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();
  const analytics = useAnalytics();

  return useMutation({
    mutationFn: async (args: OpenTrialArgs) => {
      analytics.fft.trialOpenInitiated(args);

      if (!accountAddress) {
        throw new Error("Wallet not connected");
      }

      if (primaryDex) {
        try {
          const committed = await primaryDex.openFftTrial({
            ...requireCampaignAddrs(),
            owner: accountAddress,
          });
          return { txHash: committed.hash };
        } catch (error) {
          Logger.warn("Session-key open-trial failed, falling back to wallet", {
            extra: { error },
          });
        }
      }

      const data = buildOpenTrialPayload({ ...requireCampaignAddrs(), owner: accountAddress });

      const pending = await signAndSubmitWalletTransaction({ data });
      await readDexSdk.deps.aptos.waitForTransaction({ transactionHash: pending.hash });
      return { txHash: pending.hash };
    },
    onSuccess: ({ txHash }, args) => {
      analytics.fft.trialOpenSucceeded({ ...args, txHash });
    },
    onError: (error, args) => {
      analytics.fft.trialOpenFailed({ ...args, error });
      Logger.error(error, { tags: { "fft.mutation": "open_trial" }, extra: { ...args } });
    },
    onSettled: () => {
      const account = accountAddress;
      if (!account) return;
      void readDexSdk.fundedFirstTrade
        .getActiveTrial({ account })
        .then((next) => writeActiveTrial(queryClient, account, next))
        .catch(() => undefined);
      void queryClient.invalidateQueries({
        queryKey: fundedTrialEligibilityQueryKey({ account }),
      });
    },
  });
}
